import { useState } from 'react';
import { LocationDescriptor } from 'history';
import { useTranslation } from 'react-i18next';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';
import { buildLocationState, useCurrentLocation } from 'hooks/useLocationState';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { LatLng } from 'shared/types';
import {
  COORDINATE_FORMATS,
  CoordinateFormat,
  useCoordinateFormatState,
} from 'state/useCoordinateFormatState';
import {
  COORDINATE_FORMAT_TRANS_KEYS,
  getLocationCoords,
} from './CoordinateSelector.utils';

type CoordinateFormatOption = {
  format: CoordinateFormat;
  label: string;
  text: string;
  selected: boolean;
};

export type UseCoordinateSelectorInput = {
  coordinates: LatLng;
  onExpand?: () => void;
} & (
  | { blurred?: false; paywallSource?: string }
  | { blurred: true; paywallSource: string }
);

type UseCoordinateSelector = {
  paywallLink?: LocationDescriptor;
  expanded: boolean;
  toggleExpanded: () => void;
  currentText: string;
  formats: CoordinateFormatOption[];
  copyCurrent: () => void;
  selectFormat: (format: CoordinateFormatOption) => void;
};

export const useCoordinateSelector = (
  input: UseCoordinateSelectorInput,
): UseCoordinateSelector => {
  const { coordinates, blurred = false, paywallSource, onExpand } = input;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { coordinateFormat, setCoordinateFormat } = useCoordinateFormatState();
  const { shareOrCopyText } = useShareOrCopy();
  const paywallLinkState = buildLocationState(useCurrentLocation());

  const formats = COORDINATE_FORMATS.map((format) => ({
    format,
    label: t(COORDINATE_FORMAT_TRANS_KEYS[format]),
    text: getLocationCoords({
      lat: coordinates.lat,
      lng: coordinates.lng,
      format,
    }),
    selected: format === coordinateFormat,
  }));

  const current = formats.find((option) => option.selected) ?? formats[0];

  const copyShare = (option: CoordinateFormatOption): void => {
    shareOrCopyText({
      text: option.text,
      title: option.label,
      androidDialogTitle: t('locationListItem.share'),
    });
  };

  const selectFormat = (formatOption: CoordinateFormatOption): void => {
    setCoordinateFormat(formatOption.format);
    setExpanded(false);
    copyShare(formatOption);
  };

  const toggleExpanded = (): void => {
    if (!expanded) onExpand?.();
    setExpanded(!expanded);
  };

  const getPaywallLocation = (): LocationDescriptor => {
    const { pathname, search } = getPaywallLink({
      focusedPlan: MEMBERSHIP_PLANS.yearly,
      source: paywallSource,
    });
    return { pathname, search, state: paywallLinkState };
  };

  return {
    paywallLink: blurred ? getPaywallLocation() : undefined,
    expanded: expanded && !blurred,
    toggleExpanded,
    currentText: current.text,
    formats,
    copyCurrent: () => copyShare(current),
    selectFormat,
  };
};
